import React from "react";
import { Box, ButtonBase, createSvgIcon, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ClickableContainer } from "../../utils/table-schemas";
import { MapLink } from "../Home/Home";
import { AppRoutes } from "../../config";

const MONO_FONT = "'IBM Plex Mono', monospace";
const HEADER_FONT =
  "'Inter', -apple-system, BlinkMacSystemFont, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'";

export const LandSummaryCardRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#f5f5f3",
  borderRadius: "20px",
  padding: "24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "20px",
    paddingBottom: "28px",
    gap: "12px",
  },
}));

export const LandSummaryLabel = styled(Box)({
  fontFamily: MONO_FONT,
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "18px",
  letterSpacing: "0.084px",
  textTransform: "uppercase",
  color: "#999",
});

export const LandSummaryValue = styled(Box)(() => ({
  fontFamily: MONO_FONT,
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "18px",
  color: "#000",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  marginTop: "3px",
  width: "100%",
}));

const PillButton = styled(ButtonBase)(({ disabled }) => ({
  height: "36px",
  minWidth: "64px",
  padding: "6px 16px",
  borderRadius: "20px",
  backgroundColor: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  fontFamily: MONO_FONT,
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "16.8px",
  letterSpacing: "0.084px",
  textTransform: "uppercase",
  color: "#999",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.55 : 1,
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: disabled ? "#fff" : "#fafafa",
  },
  "& svg": {
    fontSize: "16px",
    color: "#999",
  },
}));

type PillProps = {
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
};

const Pill: React.FC<PillProps> = ({ label, icon, onClick, disabled }) => (
  <PillButton onClick={disabled ? undefined : onClick} disabled={disabled}>
    {icon}
    <span>{label}</span>
  </PillButton>
);

// const MediaIcon = createSvgIcon(
//   // eslint-disable-next-line max-len
//   <svg width="19" height="21" viewBox="0 0 19 21" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path
//       d="M1.54297 0.875H16.9736C17.1087 0.875054 17.2405 0.916616 17.4053 1.09863C17.5703 1.28099 17.6415 1.46802 17.6416 1.7041V18.748C17.6416 18.9844 17.5705 19.172 17.4053 19.3545C17.2405 19.5365 17.1087 19.5781 16.9736 19.5781H1.54297C1.40783 19.5781 1.27617 19.5365 1.11133 19.3545C0.946099 19.172 0.875 18.9844 0.875 18.748V1.7041C0.875059 1.46797 0.946266 1.28102 1.11133 1.09863C1.27619 0.916526 1.40782 0.875013 1.54297 0.875Z"
//       fill="white"
//       stroke="#999999"
//       strokeWidth="1.75"
//     />
//     <path
//       d="M9.25195 4.53516C9.74314 4.53521 10.174 4.7105 10.5586 5.08984C10.9493 5.47524 11.1833 5.95304 11.2461 6.55566L11.291 6.98633L11.6865 7.16309C12.0512 7.3262 12.3454 7.58917 12.582 7.98633C12.816 8.38901 12.9336 8.8162 12.9336 9.29395C12.9336 9.96268 12.725 10.5054 12.3164 10.9619C11.9178 11.4072 11.4645 11.6045 10.9121 11.6045H7.5918C7.05094 11.6045 6.59255 11.4038 6.18164 10.9551C5.77597 10.512 5.57035 9.97478 5.57031 9.29395C5.57031 8.81423 5.68894 8.38559 5.9248 7.98145C6.1519 7.59238 6.44871 7.32795 6.81738 7.16309L7.21191 6.98633L7.25684 6.55664C7.31974 5.95798 7.54948 5.47984 7.94531 5.08887C8.33479 4.70421 8.75902 4.53516 9.25195 4.53516Z"
//       fill="white"
//       stroke="#999999"
//       strokeWidth="1.5"
//     />
//   </svg>,
//   "Media"
// );

export const SurveyIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg width="23" height="26" viewBox="0 0 23 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.53228 25.702C1.10983 25.702 0.750295 25.5507 0.447645 25.2544C0.144995 24.9517 0 24.5923 0 24.1698V8.84824C0 8.4258 0.1513 8.06638 0.447645 7.76373C0.750295 7.46108 1.10983 7.31605 1.53228 7.31605H10.9836V8.84824H1.53228V24.1698H16.8538V16.7612H18.3861V24.1698C18.3861 24.5923 18.2348 24.9517 17.9384 25.2544C17.6358 25.557 17.2762 25.702 16.8538 25.702H1.53228ZM4.18674 21.6919H5.71871V14.6679H4.18674V21.6919ZM8.42367 21.6919H9.95594V11.3199H8.42367V21.6919ZM12.6671 21.6919H14.1993V17.9151H12.6671V21.6919ZM17.3646 15.2291C16.8349 15.2291 16.3304 15.1408 15.8449 14.958C15.3594 14.7751 14.8928 14.5481 14.4515 14.2581L13.9155 14.7688C13.7641 14.9075 13.5815 14.9769 13.3798 14.9832C13.178 14.9895 12.9951 14.9201 12.8438 14.7688C12.6924 14.6175 12.6166 14.4346 12.6166 14.2329C12.6166 14.0311 12.6924 13.8483 12.8438 13.6969L13.4047 13.1358C13.1336 12.7259 12.913 12.2909 12.7554 11.8243C12.5915 11.3577 12.5159 10.8785 12.5159 10.3741C12.5159 9.03108 12.9888 7.88984 13.9346 6.94406C14.8803 5.99828 16.0279 5.52539 17.3646 5.52539H22.2197V10.3804C22.2197 11.7234 21.7468 12.8647 20.801 13.8104C19.8552 14.7562 18.7074 15.2291 17.3707 15.2291H17.3646ZM17.3646 13.6969C18.2851 13.6969 19.0733 13.3754 19.7164 12.7259C20.3595 12.0828 20.6874 11.2947 20.6874 10.3741V7.05124H17.3646C16.444 7.05124 15.6559 7.37283 15.0127 8.02226C14.3696 8.6654 14.0417 9.45353 14.0417 10.3741C14.0417 10.6641 14.0796 10.9479 14.1553 11.2316C14.231 11.5153 14.3317 11.7991 14.4515 12.0891L17.3898 9.15088C17.5411 8.99956 17.724 8.9239 17.9258 8.9239C18.1276 8.9239 18.3102 8.99956 18.4615 9.15088C18.6128 9.30221 18.6887 9.49136 18.6887 9.69943C18.6887 9.9075 18.6128 10.0967 18.4615 10.248L15.5487 13.161C15.8388 13.2997 16.1287 13.4195 16.4314 13.533C16.734 13.6465 17.043 13.6969 17.3646 13.6969Z"
      fill="#999999"
    />
  </svg>,
  "Survey"
);

export const ContractIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.27762 13.6889C8.32832 13.6889 8.37901 13.6762 8.42971 13.6509C8.48041 13.6255 8.51843 13.6002 8.54378 13.5748L14.7796 7.33878C14.9317 7.18668 15.0426 7.01557 15.1123 6.82544C15.182 6.63532 15.2168 6.4452 15.2168 6.25507C15.2168 6.05227 15.182 5.85898 15.1123 5.67519C15.0426 5.49141 14.9317 5.3298 14.7796 5.19038L11.5476 1.95827C11.4082 1.80618 11.2466 1.69527 11.0628 1.62556C10.879 1.55585 10.6857 1.52099 10.483 1.52099C10.2928 1.52099 10.1027 1.55585 9.91261 1.62556C9.7225 1.69527 9.55139 1.80618 9.3993 1.95827L9.19017 2.16741L10.597 3.59334C10.7871 3.77079 10.9266 3.97359 11.0153 4.20174C11.104 4.42988 11.1484 4.67071 11.1484 4.92421C11.1484 5.45655 10.9678 5.90334 10.6065 6.26458C10.2453 6.62581 9.79854 6.80643 9.26622 6.80643C9.01273 6.80643 8.76875 6.76207 8.53427 6.67334C8.2998 6.58462 8.09384 6.45153 7.9164 6.27408L6.49054 4.86717L3.16352 8.19433C3.12549 8.23236 3.09698 8.27355 3.07797 8.31791C3.05895 8.36228 3.04945 8.40981 3.04945 8.46051C3.04945 8.56191 3.08747 8.6538 3.16352 8.73619C3.23956 8.81857 3.32828 8.85977 3.42968 8.85977C3.48038 8.85977 3.53107 8.84709 3.58177 8.82174C3.63247 8.79639 3.67049 8.77104 3.69584 8.74569L6.28141 6.16001L7.34605 7.2247L4.7795 9.81039C4.74147 9.84841 4.71296 9.8896 4.69395 9.93397C4.67493 9.97833 4.66543 10.0259 4.66543 10.0766C4.66543 10.178 4.70345 10.2667 4.7795 10.3427C4.85554 10.4188 4.94426 10.4568 5.04566 10.4568C5.09636 10.4568 5.14705 10.4441 5.19775 10.4188C5.24845 10.3934 5.28647 10.3681 5.31182 10.3427L7.89739 7.77606L8.96203 8.84076L6.39548 11.4264C6.35745 11.4518 6.32894 11.4898 6.30993 11.5405C6.29091 11.5912 6.28141 11.6419 6.28141 11.6926C6.28141 11.794 6.31943 11.8827 6.39548 11.9588C6.47152 12.0348 6.56024 12.0729 6.66164 12.0729C6.71234 12.0729 6.75987 12.0634 6.80423 12.0443C6.84859 12.0253 6.88978 11.9968 6.9278 11.9588L9.51337 9.39211L10.578 10.4568L7.99245 13.0425C7.95442 13.0805 7.92591 13.1217 7.90689 13.1661C7.88788 13.2104 7.87838 13.258 7.87838 13.3087C7.87838 13.4101 7.91957 13.4988 8.00195 13.5748C8.08434 13.6509 8.17622 13.6889 8.27762 13.6889ZM8.25861 15.2099C7.78966 15.2099 7.37457 15.0546 7.01335 14.7441C6.65213 14.4336 6.43984 14.0438 6.37647 13.5748C5.94554 13.5115 5.58432 13.334 5.29281 13.0425C5.0013 12.751 4.82386 12.3897 4.76049 11.9588C4.32956 11.8954 3.97151 11.7148 3.68634 11.4169C3.40116 11.1191 3.22689 10.761 3.16352 10.3427C2.68189 10.2794 2.28899 10.0702 1.9848 9.71532C1.68062 9.36043 1.52853 8.94215 1.52853 8.46051C1.52853 8.20701 1.57606 7.96302 1.67111 7.72853C1.76617 7.49405 1.90242 7.28808 2.07986 7.11063L6.49054 2.71877L8.98105 5.20939C9.00639 5.24742 9.04442 5.27593 9.09511 5.29495C9.14581 5.31396 9.19651 5.32347 9.24721 5.32347C9.36128 5.32347 9.45633 5.28861 9.53238 5.2189C9.60843 5.14919 9.64645 5.05729 9.64645 4.94322C9.64645 4.89252 9.63694 4.84182 9.61793 4.79112C9.59892 4.74042 9.5704 4.70239 9.53238 4.67704L6.81373 1.95827C6.67431 1.80618 6.51272 1.69527 6.32894 1.62556C6.14516 1.55585 5.95188 1.52099 5.74909 1.52099C5.55897 1.52099 5.36885 1.55585 5.17874 1.62556C4.98862 1.69527 4.81752 1.80618 4.66543 1.95827L1.9848 4.65803C1.87073 4.77211 1.77568 4.90519 1.69963 5.05729C1.62358 5.20939 1.57289 5.36149 1.54754 5.51359C1.52219 5.66569 1.52219 5.82096 1.54754 5.97939C1.57289 6.13783 1.62358 6.28676 1.69963 6.42618L0.596962 7.5289C0.381498 7.23738 0.223069 6.91734 0.121674 6.56878C0.020279 6.22022 -0.0177441 5.86849 0.00760461 5.51359C0.0329533 5.15869 0.121674 4.8133 0.273766 4.47741C0.425858 4.14153 0.634985 3.8405 0.901146 3.57433L3.58177 0.893582C3.88596 0.602059 4.225 0.380248 4.59889 0.228149C4.97278 0.0760495 5.35618 0 5.74909 0C6.14199 0 6.52539 0.0760495 6.89928 0.228149C7.27318 0.380248 7.60588 0.602059 7.89739 0.893582L8.10651 1.10272L8.31564 0.893582C8.61983 0.602059 8.95887 0.380248 9.33276 0.228149C9.70665 0.0760495 10.0901 0 10.483 0C10.8759 0 11.2593 0.0760495 11.6332 0.228149C12.007 0.380248 12.3397 0.602059 12.6313 0.893582L15.8442 4.10667C16.1357 4.3982 16.3575 4.73408 16.5096 5.11433C16.6617 5.49458 16.7377 5.88116 16.7377 6.27408C16.7377 6.66701 16.6617 7.05042 16.5096 7.42433C16.3575 7.79824 16.1357 8.13096 15.8442 8.42248L9.60843 14.6395C9.43098 14.817 9.22503 14.9564 8.99055 15.0578C8.75608 15.1592 8.51209 15.2099 8.25861 15.2099Z"
      fill="#999999"
    />
  </svg>,
  "Contract"
);

type HeaderProps = {
  title?: string;
  location?: string;
  landId?: string;
  onMediaClick?: () => void;
  onSurveyClick?: () => void;
  onContractClick?: () => void;
  surveyDisabled?: boolean;
  contractDisabled?: boolean;
  isMobile?: boolean;
  ownerAddress?: string;
};

export const LandSummaryHeader: React.FC<HeaderProps> = ({
  title,
  location,
  landId,
  // onMediaClick,
  // onSurveyClick,
  onContractClick,
  // surveyDisabled,
  contractDisabled,
  ownerAddress,
}) => {
  const navigate = useNavigate();
  const truncatedOwner = ownerAddress ? `${ownerAddress.substring(0, 6)}...${ownerAddress.slice(-4)}` : "";
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "12px", sm: "16px" },
        alignItems: { xs: "flex-start", sm: "flex-start" },
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: { xs: "none", sm: "none" }, flexDirection: "column", gap: "6px", minWidth: 0 }}>
        <MapLink
          sx={{
            color: "inherit",
            textDecoration: "none",
            fontFamily: HEADER_FONT,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            minWidth: 0,
          }}
          to={AppRoutes.Explore.path + "?id=" + landId}
        >
          <ClickableContainer sx={{ fontFamily: HEADER_FONT, display: "flex", flexDirection: "column" }}>
            {title && (
              <Box
                sx={{
                  fontFamily: HEADER_FONT,
                  fontWeight: 700,
                  fontSize: { xs: "22px", sm: "26px" },
                  lineHeight: 1.1,
                  color: "#000",
                  wordBreak: "break-word",
                }}
              >
                {title}
              </Box>
            )}
            {location && (
              <Box
                sx={{
                  fontFamily: HEADER_FONT,
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "19.2px",
                  color: "#000",
                }}
              >
                {location}
              </Box>
            )}
          </ClickableContainer>
        </MapLink>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          alignSelf: { xs: "stretch", sm: "flex-start" },
          width: "100%",
          marginTop: { xs: "4px", sm: 0 },
          justifyContent: "space-between",
        }}
      >
        {/* <Pill label="Media" icon={<MediaIcon />} onClick={onMediaClick} disabled={!onMediaClick} />
        <Pill
          label="Survey"
          icon={<SurveyIcon />}
          onClick={onSurveyClick}
          disabled={surveyDisabled || !onSurveyClick}
        /> */}

        {ownerAddress && (
          <Pill
            label={`Owned By ${truncatedOwner}`}
            icon={<></>}
            onClick={() => navigate(AppRoutes.UserWallet.path.replace(":address", ownerAddress.toLowerCase()))}
          />
        )}
        <Pill
          label="Contract"
          icon={<ContractIcon />}
          onClick={onContractClick}
          disabled={contractDisabled || !onContractClick}
        />
      </Box>
    </Box>
  );
};

type FieldProps = {
  label: string;
  children: React.ReactNode;
  align?: "left" | "right";
};

export const LandSummaryField: React.FC<FieldProps> = ({ label, children, align = "left" }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      alignItems: align === "right" ? { xs: "flex-start", sm: "flex-start" } : "flex-start",
      width: "100%",
    }}
  >
    <LandSummaryLabel>{label}</LandSummaryLabel>
    <LandSummaryValue>{children}</LandSummaryValue>
  </Box>
);
